import { useState, useEffect, useCallback, useRef } from 'react';
import { PriceMap } from '../types';
import { cgGet } from '../utils/coingecko';

const CACHE_KEY = 'mycryptoplanner_prices';
const CACHE_TTL = 60_000;

interface CacheEntry {
  prices: PriceMap;
  fetchedAt: number;
}

function loadCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch { return null; }
}

function saveCache(prices: PriceMap) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ prices, fetchedAt: Date.now() }));
}

export function useCoinPrices(coinIds: string[]) {
  const [prices,      setPrices]      = useState<PriceMap>(() => loadCache()?.prices ?? {});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const coinIdsRef = useRef(coinIds);
  useEffect(() => { coinIdsRef.current = coinIds; }, [coinIds]);

  const fetchPrices = useCallback(async (force = false) => {
    const ids = coinIdsRef.current;
    if (ids.length === 0) { setLoading(false); return; }

    const cache     = loadCache();
    const allCached = ids.every((id) => cache?.prices[id] !== undefined);

    if (!force && allCached && cache && Date.now() - cache.fetchedAt < CACHE_TTL) {
      setPrices(cache.prices);
      setLastUpdated(new Date(cache.fetchedAt));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const { data } = await cgGet('/simple/price', {
        params: { ids: ids.join(','), vs_currencies: 'usd', include_24hr_change: 'true' },
        timeout: 10_000,
      });

      const fetched: PriceMap = {};
      for (const [id, val] of Object.entries(data as Record<string, Record<string, number>>)) {
        fetched[id] = { usd: val.usd ?? 0, usd_24h_change: val.usd_24h_change ?? 0 };
      }

      const merged = { ...(loadCache()?.prices ?? {}), ...fetched };
      saveCache(merged);
      setPrices(merged);
      setLastUpdated(new Date());
    } catch {
      const stale = loadCache();
      if (stale) { setPrices(stale.prices); setLastUpdated(new Date(stale.fetchedAt)); }
      setError('Failed to fetch live prices. Showing cached data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const idsKey = [...coinIds].sort().join(',');
  useEffect(() => {
    fetchPrices(false);
    const interval = setInterval(() => fetchPrices(true), 60_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, fetchPrices]);

  return { prices, loading, error, lastUpdated, refresh: () => fetchPrices(true) };
}
